import React, { useState, useMemo, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import './MapaCanchas.css';

const COLORES_ESPECTADOR = [
    '#2244aa', '#cc3333', '#4488dd', '#e05555',
    '#1a3388', '#aa2222', '#ffffff', '#d44040',
    '#3355bb', '#222222', '#6699cc', '#c74848',
    '#5599ee', '#b52b2b', '#eeeeee', '#333333',
];

const CANCHAS_LAYOUT = [
    // zona superior (arriba de Vito Dumas)
    { id: 'C12', zona: 'superior', bloque: 4, fila: 0 },
    { id: 'C9', zona: 'superior', bloque: 3, fila: 1 },
    { id: 'C10', zona: 'superior', bloque: 3, fila: 1 },
    { id: 'C11', zona: 'superior', bloque: 3, fila: 1 },
    // zona inferior (debajo de Vito Dumas)
    { id: 'C5', zona: 'inferior', bloque: 2, fila: 0 },
    { id: 'C6', zona: 'inferior', bloque: 2, fila: 0 },
    { id: 'C7', zona: 'inferior', bloque: 2, fila: 0 },
    { id: 'C8', zona: 'inferior', bloque: 2, fila: 0 },
    { id: 'C1', zona: 'inferior', bloque: 1, fila: 1 },
    { id: 'C2', zona: 'inferior', bloque: 1, fila: 1 },
    { id: 'C3', zona: 'inferior', bloque: 1, fila: 1 },
    { id: 'C4', zona: 'inferior', bloque: 1, fila: 1 },
];

const MapaCanchas = ({ show, onHide, obtenerPartidosEnCurso, canchasDisponibles, slotsMatriz }) => {
    const [horaTest, setHoraTest] = useState('');
    const [tick, setTick] = useState(0);
    const [vista, setVista] = useState('mapa');

    // Siempre arrancar en mapa al abrir
    React.useEffect(() => {
        if (show) setVista('mapa');
    }, [show]);

    useEffect(() => {
        const interval = setInterval(() => setTick(t => t + 1), 30000);
        return () => clearInterval(interval);
    }, []);

    const partidos = useMemo(() => {
        if (!obtenerPartidosEnCurso) return [];
        return obtenerPartidosEnCurso(horaTest || null);
    }, [obtenerPartidosEnCurso, horaTest, slotsMatriz, canchasDisponibles]);

    // Mapear codActivo -> partido en curso
    const partidosPorCancha = useMemo(() => {
        const map = {};
        partidos.forEach(p => {
            map[p.codCancha] = p;
        });
        return map;
    }, [partidos]);

    // Convertir "C3" -> "CANCHA03"
    const toCodActivo = (canchaId) => {
        const num = canchaId.replace('C', '');
        return `CANCHA${num.padStart(2, '0')}`;
    };

    const esDobles = (tipoJuego) => {
        return tipoJuego && (tipoJuego.startsWith('DOBLES'));
    };

    const getMinutosRestantes = (horaFin) => {
        if (!horaFin) return null;
        const [hh, mm] = horaFin.split(':').map(Number);
        let ahora;
        if (horaTest) {
            const [ah, am] = horaTest.split(':').map(Number);
            ahora = ah * 60 + am;
        } else {
            const now = new Date();
            ahora = now.getHours() * 60 + now.getMinutes();
        }
        const fin = hh * 60 + mm;
        const diff = fin - ahora;
        return diff > 0 ? diff : 0;
    };

    const getNombreCorto = (jugador) => {
        return jugador?.shortName || jugador?.nombre || '';
    };

    const renderCancha = (canchaId) => {
        const partido = partidosPorCancha[toCodActivo(canchaId)];
        const enUso = !!partido;
        const dobles = enUso && esDobles(partido.tipoJuego);
        const jugadores = partido?.jugadores || [];

        const minutosRestantes = enUso ? getMinutosRestantes(partido.horaFin) : null;

        return (
            <div key={canchaId} className="mapaCanchasCancha canchaLibre">
                <div className="canchaLineas" />
                <div className="canchaLineasSingles" />
                <div className="mapaCanchasChanchaId">{canchaId}</div>
                {enUso && minutosRestantes !== null && (
                    <div className="mapaCanchasTimer">{minutosRestantes}&#39;</div>
                )}
                {enUso && (
                    <>
                        <span className="pelotaCancha" />
                        {/* J0: abajo-derecha (fondo) */}
                        <div className="jugadorPos jugadorAbajoDer">
                            {getNombreCorto(jugadores[0]) || partido.usuario || ''}
                        </div>
                        {/* J1: abajo-izquierda (red) - mismo equipo que J0 */}
                        {dobles && jugadores[1] && (
                            <div className="jugadorPos jugadorAbajoIzq">
                                {getNombreCorto(jugadores[1])}
                            </div>
                        )}
                        {/* J2: arriba-izquierda (fondo) */}
                        {jugadores[dobles ? 2 : 1] && (
                            <div className="jugadorPos jugadorArribaIzq">
                                {getNombreCorto(jugadores[dobles ? 2 : 1])}
                            </div>
                        )}
                        {/* J3: arriba-derecha (red) - mismo equipo que J2 */}
                        {dobles && jugadores[3] && (
                            <div className="jugadorPos jugadorArribaDer">
                                {getNombreCorto(jugadores[3])}
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    const renderLista = () => {
        const ahora = new Date();
        const horaActual = `${String(ahora.getHours()).padStart(2, '0')}:${String(ahora.getMinutes()).padStart(2, '0')}`;
        const fechaActual = ahora.toLocaleDateString('es-AR');

        return (
            <div className="mapaCanchasListaContainer">
                <div className="mb-3 text-center" style={{ marginTop: '25px' }}>
                    <small style={{ color: '#aaa' }}>
                        Fecha y hora actual: <strong style={{ color: '#ccc' }}>{fechaActual} {horaActual}</strong>
                    </small>
                </div>
                {partidos.length === 0 ? (
                    <p style={{ color: '#aaa' }} className="text-center mb-0">
                        No hay partidos en curso en este momento
                    </p>
                ) : (
                    <div className="mapaCanchasListaGrid">
                        {partidos.map((partido) => (
                            <div key={partido.reservaId || partido.codCancha} className="mapaCanchasListaCard">
                                <div className="mb-2">
                                    <strong style={{ color: '#28a745' }}>{partido.codCancha}</strong> - {partido.cancha}
                                </div>
                                <div className="mb-2">
                                    <small style={{ color: '#aaa' }}>Horario:</small> <strong>{partido.horaInicio} - {partido.horaFin}</strong>
                                </div>
                                <div>
                                    <small style={{ color: '#aaa' }}>Jugadores:</small>
                                    {partido.jugadores.length > 0 ? (
                                        <ul className="mb-0 mt-1" style={{ paddingLeft: '20px' }}>
                                            {partido.jugadores.map((jugador, idx) => (
                                                <li key={idx} style={{ fontSize: '0.9em' }}>
                                                    {jugador.nombre}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <span style={{ color: '#888', marginLeft: '8px' }}>Sin jugadores registrados</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered dialogClassName="mapaCanchasModal" style={{ zIndex: 10000 }} backdropClassName="mapaCanchasBackdrop">
            <Modal.Body style={{ padding: 0, position: 'relative' }}>
                <button className="minimizarBtn" onClick={onHide} style={{ position: 'absolute', top: 5, right: 5, zIndex: 1, padding: '0.2rem 0.6rem', fontSize: '1rem' }}>X</button>

                {/* Selector de hora para testing */}
                <div className="mapaCanchasHoraTest">
                    <label>Hora simulada:</label>
                    <input
                        type="time"
                        value={horaTest}
                        onChange={e => setHoraTest(e.target.value)}
                    />
                    {horaTest && <button onClick={() => setHoraTest('')}>Ahora</button>}
                    <button
                        className="mapaCanchasVistaBtn"
                        onClick={() => setVista(vista === 'mapa' ? 'lista' : 'mapa')}
                    >
                        {vista === 'mapa' ? 'Lista' : 'Mapa'}
                    </button>
                </div>

                {vista === 'mapa' ? (
                    <div className="mapaCanchasContainer">
                        {/* Zona superior con sidebars hasta Vito Dumas */}
                        <div className="mapaCanchasTopRow">
                            {/* Av. Altair - lado izquierdo */}
                            <div className="mapaCanchasVerdeIzq">
                                <span>Av. Altair</span>
                            </div>

                            <div className="mapaCanchasTopCenter">
                                {/* === ARRIBA DE LA CALLE === */}
                                <div className="mapaCanchasZonaSuperior">
                                    {/* Bloque 4: C12 */}
                                    <div className="mapaCanchasBloque bloque4">
                                        <div className="mapaCanchasFila">
                                            {renderCancha('C12')}
                                        </div>
                                    </div>

                                    {/* Bloque 3: C9 C10 C11 */}
                                    <div className="mapaCanchasBloque bloque3">
                                        <div className="mapaCanchasFila">
                                            {renderCancha('C9')}
                                            {renderCancha('C10')}
                                            {renderCancha('C11')}
                                        </div>
                                    </div>
                                </div>

                                {/* Calle Vito Dumas */}
                                <div className="mapaCanchasCalle">
                                    <span>Vito Dumas</span>
                                </div>
                            </div>

                            {/* Marina - lado derecho */}
                            <div className="mapaCanchasMar">
                                <span>Marina</span>
                            </div>
                        </div>

                        {/* Zona inferior sin sidebars */}
                        <div className="mapaCanchasZonaInferior">
                            {/* Bloque 2: C5 C6 C7 C8 */}
                            <div className="mapaCanchasBloque bloque2">
                                <div className="mapaCanchasFila">
                                    {renderCancha('C5')}
                                    {renderCancha('C6')}
                                    {renderCancha('C7')}
                                    {renderCancha('C8')}
                                </div>
                            </div>

                            {/* Bloque 1: C1 C2 C3 C4 + Grada */}
                            <div className="mapaCanchasBloque bloque1">
                                <div className="mapaCanchasFila">
                                    {renderCancha('C1')}
                                    {renderCancha('C2')}
                                    {renderCancha('C3')}
                                    {renderCancha('C4')}
                                </div>
                                <div className="mapaCanchasGrada">
                                    {Array.from({ length: 6 }, (_, col) => (
                                        <div key={col} className="gradaColumna">
                                            {Array.from({ length: 12 - col }, (_, row) => (
                                                <span key={row} className="gradaEspectador" style={{ backgroundColor: COLORES_ESPECTADOR[(col * 7 + row * 3 + col * row) % COLORES_ESPECTADOR.length] }} />
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    renderLista()
                )}
            </Modal.Body>
        </Modal>
    );
};

export default MapaCanchas;
